import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, PenSquare, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoWordmark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { title: "", category: "", excerpt: "", body: "" };

const inputCls =
  "w-full bg-white border border-[#e6e1d5] rounded-xl px-4 py-3.5 text-sm text-[#1b2a4e] placeholder:text-[#8a93a8] focus:outline-none focus:ring-2 focus:ring-[#a8824f] focus:border-transparent transition-all";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

export default function AdminInsights() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState(null);
  const [mode, setMode] = useState("list");
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user === null) navigate("/admin/login");
  }, [user, navigate]);

  const load = () => {
    axios
      .get(`${API}/admin/insights`, { withCredentials: true })
      .then((res) => setArticles(res.data.articles || []))
      .catch(() => setArticles([]));
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (user === undefined || user === null) {
    return (
      <div className="min-h-screen bg-[#faf9f6] flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#a8824f]" />
      </div>
    );
  }

  const startCreate = () => {
    setForm(EMPTY);
    setEditingId(null);
    setMode("editor");
  };

  const startEdit = (a) => {
    setForm({ title: a.title, category: a.category, excerpt: a.excerpt, body: a.body });
    setEditingId(a.id);
    setMode("editor");
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/insights/${editingId}`, form, { withCredentials: true });
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/admin/insights`, form, { withCredentials: true });
        toast.success("Article published", { description: "It is now live in the Insights section." });
      }
      setMode("list");
      load();
    } catch (err) {
      toast.error("Could not save the article");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/insights/${a.id}`, { withCredentials: true });
      toast.success("Article deleted");
      load();
    } catch {
      toast.error("Could not delete the article");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-insights-page" className="min-h-screen bg-[#faf9f6]">
      <header className="bg-white border-b border-[#e6e1d5] px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <LogoWordmark dark />
          <div className="flex items-center gap-5">
            <span data-testid="admin-user-email" className="hidden sm:block text-xs text-[#8a93a8] font-mono-alt tracking-wide">
              {user.email}
            </span>
            <a data-testid="admin-view-site-link" href="/" className="text-xs text-[#45506b] hover:text-[#a8824f] transition-colors">
              View site
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs font-semibold text-[#1b2a4e] hover:text-[#b0483c] transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {mode === "list" && (
          <>
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="font-mono-alt text-[10px] uppercase tracking-[0.35em] text-[#a8824f] mb-2">
                  Insights Studio
                </p>
                <h1 className="font-display text-3xl text-[#1b2a4e]">Your published briefings</h1>
              </div>
              <button
                data-testid="admin-new-article-button"
                onClick={startCreate}
                className="flex items-center gap-2 bg-[#1b2a4e] hover:bg-[#a8824f] text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-300"
              >
                <Plus size={16} /> New Article
              </button>
            </div>

            {articles === null ? (
              <p data-testid="admin-articles-loading" className="text-sm text-[#8a93a8] font-mono-alt uppercase tracking-[0.2em]">Loading...</p>
            ) : articles.length === 0 ? (
              <p data-testid="admin-articles-empty" className="text-sm text-[#45506b]">No articles yet — publish your first briefing.</p>
            ) : (
              <div data-testid="admin-articles-list" className="space-y-4">
                {articles.map((a) => (
                  <div
                    key={a.id}
                    data-testid={`admin-article-row-${a.slug}`}
                    className="bg-white border border-[#e6e1d5] rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
                  >
                    <div className="min-w-0">
                      <span className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-1.5">
                        {a.category} · {fmtDate(a.published_at)}
                      </span>
                      <h3 className="font-display text-lg text-[#1b2a4e] leading-snug truncate">{a.title}</h3>
                      <p className="text-sm text-[#45506b] truncate mt-1 max-w-xl">{a.excerpt}</p>
                    </div>
                    <div className="flex items-center gap-2.5 shrink-0">
                      <button
                        data-testid={`admin-edit-${a.slug}`}
                        onClick={() => startEdit(a)}
                        className="flex items-center gap-1.5 text-xs font-semibold text-[#1b2a4e] hover:text-[#a8824f] px-4 py-2.5 rounded-full border border-[#e6e1d5] hover:border-[#a8824f]/50 transition-colors"
                      >
                        <PenSquare size={13} /> Edit
                      </button>
                      <button
                        data-testid={`admin-delete-${a.slug}`}
                        onClick={() => remove(a)}
                        className="flex items-center gap-1.5 text-xs font-semibold text-[#b0483c] px-4 py-2.5 rounded-full border border-[#e6e1d5] hover:border-[#b0483c]/50 transition-colors"
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {mode === "editor" && (
          <form data-testid="admin-article-form" onSubmit={save} className="bg-white border border-[#e6e1d5] rounded-3xl p-8 sm:p-10 shadow-[0_24px_70px_rgba(27,42,78,0.08)]">
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-display text-2xl text-[#1b2a4e]">
                {editingId ? "Edit article" : "New article"}
              </h1>
              <button
                data-testid="admin-cancel-edit-button"
                type="button"
                onClick={() => setMode("list")}
                className="text-xs font-semibold text-[#45506b] hover:text-[#a8824f] transition-colors"
              >
                Cancel
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <label htmlFor="admin-title-input" className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2">Title</label>
                <input
                  id="admin-title-input"
                  data-testid="admin-title-input"
                  required
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. What the FCA's latest guidance means for your firm"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="admin-category-input" className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2">Category</label>
                <input
                  id="admin-category-input"
                  data-testid="admin-category-input"
                  required
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  placeholder="e.g. FCA Conduct"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="admin-excerpt-input" className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2">Short summary</label>
                <input
                  id="admin-excerpt-input"
                  data-testid="admin-excerpt-input"
                  required
                  value={form.excerpt}
                  onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
                  placeholder="One or two sentences shown on the card"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="admin-body-input" className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2">
                  Article text <span className="normal-case tracking-normal text-[#8a93a8]">(blank line between paragraphs)</span>
                </label>
                <textarea
                  id="admin-body-input"
                  data-testid="admin-body-input"
                  required
                  rows={12}
                  value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  placeholder="Write the briefing here..."
                  className={`${inputCls} resize-y leading-relaxed`}
                />
              </div>
            </div>
            <button
              data-testid="admin-save-article-button"
              type="submit"
              disabled={busy}
              className="mt-8 flex items-center gap-2.5 bg-[#1b2a4e] hover:bg-[#a8824f] disabled:opacity-50 text-white text-sm font-semibold px-9 py-4 rounded-full transition-all duration-300"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {busy ? "Saving..." : editingId ? "Save Changes" : "Publish Article"}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
